import envConfig from 'brand/env';

import cloneDeep from 'lodash/cloneDeep';

import colors from 'vuetify/lib/util/colors';

const tempIdPrefix = 'TEMP-ID-';

export const randomHex = (len) => {
  const maxlen = 8;
  const min = 16 ** (Math.min(len, maxlen) - 1);
  const max = (16 ** Math.min(len, maxlen)) - 1;
  const n = Math.floor(Math.random() * (max - min + 1)) + min;
  let r = n.toString(16);

  while (r.length < len) {
    r += randomHex(len - maxlen);
  }
  return r;
};

export const genTempID = (length = 20) => (tempIdPrefix + randomHex(length));

export const findTempID = (id) => {
  if (typeof id === 'string') return id.startsWith(tempIdPrefix);
  if (typeof id === 'number') return false;
  return false;
};

/* eslint prefer-template: "off" */
/* eslint no-bitwise: "off" */
/* eslint no-plusplus: "off" */
export const stringToHslColor = (str, s, l) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const h = hash % 360;
  return 'hsl(' + h + ', ' + s + '%, ' + l + '%)';
};

const hexToHsl = (hex) => {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  var r = parseInt(result[1], 16);
  var g = parseInt(result[2], 16);
  var b = parseInt(result[3], 16);

  r /= 255, g /= 255, b /= 255;
  var max = Math.max(r, g, b), min = Math.min(r, g, b);
  var h, s, l = (max + min) / 2;

  if(max == min){
      h = s = 0; // achromatic
  } else {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch(max) {
          case r: h = (g - b) / d + (g < b ? 6 : 0); break;
          case g: h = (b - r) / d + 2; break;
          case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
  }

  s = s*100;
  s = Math.round(s);
  l = l*100;
  l = Math.round(l);
  h = Math.round(360*h);

  return { h,s,l };
}

export const muteColor = (hex,value) => {
  const { h, s } = hexToHsl(hex);
  return `hsl(${h},${s}%,${value}%)`
}

export const findEnv = (config, url) => Object.keys(config)
  .find((v) => config[v].url === url) || 'prod';

export const getWorkspaceId = () => localStorage.getItem('yabbr-organisation');

export const yabbrAuthRedirect = () => {
  const env = envConfig[findEnv(envConfig, window.top.location.origin)];
  window.location.href = `${env.yabbrPortal}/#/?referrer=${encodeURIComponent(window.location.href)}&reason=get_tokens&samepage=true`;
};

export const getChangeWorkspaceLink = () => {
  const env = envConfig[findEnv(envConfig, window.top.location.origin)];
  // eslint-disable-next-line
  return `${env.yabbrPortal}/#/change-workspace?referrer=${encodeURIComponent(window.location.href)}&reason=change_workspace&samepage=true&app=workflows`;
};

export const getYabbrLink = () => {
  const env = envConfig[findEnv(envConfig, window.top.location.origin)];
  return `${env.yabbrPortal}/#/`;
};

export const insertText = (input, newText) => {
  input.focus();
  let cursorStart = input.selectionStart;
  let cursorEnd = input.selectionEnd;
  const text = input.value;
  const start = text.substring(0, cursorStart);
  const end = text.substring(cursorEnd);
  const insertedText = `${start}${newText}${end}`;
  if (!document.execCommand('insertText', false, newText)) {
    // Firefox doesn't support the above command but it will return false
    // Keeping the above command as it enables undo/redo on other browsers
    /* eslint no-param-reassign: "off" */
    input.value = insertedText;
  }

  /* eslint no-multi-assign: "off" */
  cursorStart = cursorEnd = cursorStart + newText.length;
  return { insertedText, cursorStart, cursorEnd };
};

export const setObj = (obj, path, val) => {
  let refPoint = obj;
  const pathParts = path.split('.');
  pathParts.forEach((part, partIdx) => {
    if (partIdx === pathParts.length - 1) {
      refPoint[part] = { ...refPoint[part], ...val };
      return;
    }

    if (!(part in refPoint)) {
      refPoint[part] = {};
    }

    refPoint = refPoint[part];
  });
};

export const copyToClipboard = (text) => navigator.clipboard.writeText(text)
  .catch((err) => console.warn('Failed to copy text to clipboard: ', err));

export const exportJsonFile = (json, filename) => {
  const jsonContent = `data:text/json; charset=utf-8,${JSON.stringify(json, null, 2)}`;
  const encodedUri = encodeURI(jsonContent);

  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `${filename}.json`);
  document.body.appendChild(link);
  link.click();
};

export const cloneFilter = (object, filters=[]) => {
  const cloned = cloneDeep(object);
  filters.forEach((path) => {
    let obj = cloned;
    const splitPath = path.split('.');
    const lastProp = splitPath.pop();
    splitPath.forEach((property) => obj = obj[property] || {});
    delete obj[lastProp];
  });
  return cloned;
};

export const getFileExtension = name => name.substr(name.lastIndexOf('.')).toLowerCase();

export const generateApiHeaders = ({
  customHeaders = {},
  noContentType = false,
  noHeaders = null,
}) => {
  const headers = noHeaders ? {} : {
    ...(!noContentType && { 'content-type': 'application/json' }),
    ...customHeaders,
  };

  if (localStorage.getItem('yabbr-token')) {
    headers['x-token'] = localStorage.getItem('yabbr-token');
  }

  if (localStorage.getItem('yabbr-mfa')) {
    headers['x-mfa'] = localStorage.getItem('yabbr-mfa');
  }

  return headers;
};

export const endpointToDisplay = (display, endpoint, name) => {
  const env = envConfig[findEnv(envConfig, window.top.location.origin)];

  switch (display) {
    case 'workflow':
      return {
        type: 'https',
        ...(!!name && { name }),
        url: env[endpoint.loc || 'ybApi'] + endpoint.endpoint,
        method: endpoint.method,
        headers: generateApiHeaders(endpoint),
        ...(!!endpoint.data && { body: endpoint.data }),
      };

    case 'curl': {
      const headerStr = Object.entries(generateApiHeaders(endpoint))
        .map(([key, value]) => `-H '${key}: ${value}'`)
        .join(' ');

      const bodyStr = !endpoint.data ? '' : `-d '${JSON.stringify(endpoint.data)}'`;

      return `curl ${env[endpoint.loc || 'ybApi'] + endpoint.endpoint} -X ${endpoint.method} ${headerStr} ${bodyStr} -s`;
    }

    case 'info':
    default:
      return {
        url: env[endpoint.loc || 'ybApi'] + endpoint.endpoint,
        method: endpoint.method,
        ...(!!endpoint.data && { body: endpoint.data }),
      };
  }
};

export const handleError = (label, error) => {
  const debugLogs = localStorage.getItem('yabbr-debug-logs');
  if (debugLogs) {
    console.error(label, error);
  }

  // submit to some error tracking service here
};

export const timeUntil = (futureTime, currentTime = new Date()) => {
  const future = new Date(futureTime);
  const current = new Date(currentTime);
  const diff = future - current;
  if (diff <= 0) return "in the past";
  const seconds = Math.round(diff / 1000);
  const minutes = Math.round(seconds / 60);
  const hours = minutes / 60 < 1 ? 0 : Math.round(minutes / 60);
  const days = hours / 24 < 1 ? 0 : Math.round(hours / 24);
  if (days > 0) {
      return `in ${days} day${days > 1 ? 's' : ''}`;
  } else if (hours > 0) {
      return `in ${hours} hour${hours > 1 ? 's' : ''}`;
  } else if (minutes > 0) {
      return `in ${minutes} minute${minutes > 1 ? 's' : ''}`;
  } else {
      return `in ${seconds} second${seconds > 1 ? 's' : ''}`;
  }
}

export const addTime = (inputDate,{ milliseconds = 0, seconds = 0, minutes = 0, hours = 0, days = 0, months = 0, years = 0 }) => {
  const date = new Date(inputDate);
  date.setMilliseconds(date.getMilliseconds() + milliseconds);
  date.setSeconds(date.getSeconds() + seconds);
  date.setMinutes(date.getMinutes() + minutes);
  date.setHours(date.getHours() + hours);
  date.setDate(date.getDate() + days);
  date.setMonth(date.getMonth() + months);
  date.setFullYear(date.getFullYear() + years);
  return date;
}

export const toFormat = (date, format) => {
  const jsDate = new Date(date);
  let options = {};
  switch (format) {
    case 'full':
      options = { 
        hour: '2-digit', 
        minute: '2-digit' ,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour12: false,
        fractionalSecondDigits: 3
      }; 
      break;
    case 'datetime':
      options = { 
        hour: '2-digit', 
        minute: '2-digit' ,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour12: false,
      }
      break;
    case 'date':
    default:
      break;
  }
  return jsDate.toLocaleDateString('en-CA',options).replace(',',' ');
}



export const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + (( hash << 5) - hash);
  }
  return hash;
};

export const hyphenate = (str, matching = /[A-Z0-9]+/g) => {
  const hyphenPoints = [...str.matchAll(matching)];
  if (!hyphenPoints || !hyphenPoints.length) return str;

  const split = str.split('');
  hyphenPoints.forEach((point, idx) => {
    split.splice(point.index + idx, 0, '-');
  });

  return split.join('').toLowerCase();
};

const colorNames = [
  ...['red', 'pink', 'purple', 'indigo', 'blue', 'teal'].map(key => `${key}-lighten-4`),
  ...['light-blue', 'cyan', 'green', 'light-green', 'amber', 'orange', 'deep-orange'].map(key => `${key}-lighten-3`),
  ...['deep-purple', 'lime', 'yellow'].map(key => `${key}-lighten-2`),
]

const colorDict = Object.fromEntries(Object.entries(colors)
  .map(([base, variants]) => Object.entries(variants).map(([variant,hex]) => [`${hyphenate(base)}-${hyphenate(variant)}`,hex]))
  .flat());

export const tagColor = (tag,muted=false) => {
  const name = colorNames[Math.abs(hashString(tag)) % colorNames.length];
  if (muted) return muteColor(colorDict[name],90);
  return name;
};
